import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	useTheme,
	ListItemText,
	useMediaQuery,
	TableHead,
	TableRow,
	TableCell,
	Box,
	ButtonGroup,
	Button,
} from '@mui/material';

import { Face as Avatar } from '@mui/icons-material';

import ResponsiveTable from 'components/ResponsiveTable';
import FullLayout from 'components/layouts/FullLayout';
import { CustomTooltipProps } from 'components/tooltips/CustomTooltip/CustomTooltip';

import { rowsPerPageOptions } from 'config/constants';
import SyncReportModal from 'components/SyncReportModal';
import { fetchExportSyncReport, fetchSyncReport } from 'lib/models/syncReport';
//import { useNavigate } from 'react-router';
//import { sanitizedInput } from 'lib/helpers';
//import useUser from 'hooks/useUser';

type Props = {
	loading?: boolean;
	editUrl?: string;
	syncReports: PaginateSource<PartialAccessOffline>;
	paper?: boolean;
	disabled?: boolean;
	//refetch?: () => {};
	//customAction?: CustomTooltipProps;
	refetch: () => void;
	customAction?: CustomTooltipProps;
	handleChangePage: (_: unknown, newPage: number) => any;
	handleChangeRowsPerPage: (comment: React.ChangeEvent<HTMLInputElement>) => any;
	page: number;
	rowsPerPage: number;
	onSearch: (value: string) => void;
	initialValue: string;
};

export default function SyncReportTable(props: Props) {
	const { t } = useTranslation();
	//const { user } = useUser();

	//const navigate = useNavigate();
	const { rowsPerPage, page, handleChangeRowsPerPage, handleChangePage } = props;

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const [selectedReport, setSelectedReport] = useState<AccessOffline | null>(null);

	const [modalOpen, setModalOpen] = useState(false);

	const handleOpenModal = async (syncReport: PartialAccessOffline) => {
		try {
			const id = syncReport._id ? syncReport._id : '';
			const response = await fetchSyncReport(id);
			setSelectedReport(response);
			setModalOpen(true);
		} catch (error) {
			console.error(error);
		}
	};
	if (false) {
		const testVar = {} as PartialAccessOffline;
		handleOpenModal(testVar);
	}

	const handleCloseModal = () => {
		setModalOpen(false);
		setSelectedReport(null);
	};

	//const searchValue = sanitizedInput(search);

	//const vehicles = props.vehicles.filter((vehicle) => {
	//	return (
	//		!searchValue ||
	//		sanitizedInput(vehicle.code).includes(searchValue) ||
	//		sanitizedInput(vehicle.description).includes(searchValue)
	//	);
	//});

	//function onEdit(selected: Vehicle) {
	//	const { _id } = selected;
	//	navigate(`/${props.editUrl || 'parking/vehicles'}/${_id}`);
	//}

	return (
		<FullLayout paper={props.paper}>
			{!props.disabled && (
				<ResponsiveTable
					elements={
						isMobile
							? props.syncReports.elements
							: props.syncReports.elements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
					}
					list={{
						primaryKey: '_id',
						avatar: <Avatar />,
						renderListItemText: (syncReport) => {
							return (
								<ListItemText
									onClick={() => false}
									primary={`# ${t('common:process_sync')}: ${syncReport.processSyncId || ''}`}
									secondary={
										<>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'start',
													'& > *': {
														m: 1,
													},
												}}
											>
												{`${syncReport.userProcessSync.first_name || ''}`}{' '}
												{`${syncReport.userProcessSync.last_name || ''}`}
											</Box>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'start',
													'& > *': {
														m: 1,
													},
												}}
											>
												{`${t('common:date_process_sync')}: ${syncReport.createdAt || ''}`}
											</Box>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'start',
													'& > *': {
														m: 1,
													},
												}}
											>
												{`${t('common:total_mov_sync')}: ${syncReport.totalItems || ''}`}
											</Box>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'start',
													'& > *': {
														m: 1,
													},
												}}
											>
												{`${t('common:success_mov_sync')}: ${syncReport.successCount || ''}`}
											</Box>
											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'start',
													'& > *': {
														m: 1,
													},
												}}
											>
												{`${t('common:error_mov_sync')}: ${syncReport.errorCount || 0}`}
											</Box>

											<Box
												sx={{
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'start',
													'& > *': {
														m: 1,
													},
												}}
											>
												<ButtonGroup size="small" aria-label="Small button group">
													≈
													<Button
														key="two"
														color="error"
														onClick={() => fetchExportSyncReport(syncReport._id ? syncReport._id : '')}
														variant="outlined"
													>
														{t(`common:download`)} csv
													</Button>
												</ButtonGroup>
											</Box>
										</>
									}
								/>
							);
						},
					}}
					table={{
						renderHead: () => (
							<TableHead>
								<TableRow>
									<TableCell align="left" padding="normal">
										#{t('common:process_sync')}
									</TableCell>
									<TableCell align="left" padding="normal">
										{t('common:date_process_sync')}
									</TableCell>
									<TableCell align="left" padding="normal">
										{t('common:user_process_sync')}
									</TableCell>
									<TableCell align="left" padding="normal">
										{t('common:total_mov_sync')}
									</TableCell>
									<TableCell align="left" padding="normal">
										{t('common:success_mov_sync')}
									</TableCell>
									<TableCell align="left" padding="normal">
										{t('common:error_mov_sync')}
									</TableCell>
									<TableCell align="left" padding="normal">
										{t('common:accion')}
									</TableCell>
								</TableRow>
							</TableHead>
						),
						renderRow: (syncReport) => {
							return (
								<TableRow tabIndex={-1} key={syncReport._id}>
									<TableCell>{syncReport.processSyncId}</TableCell>
									<TableCell>
										{new Date(syncReport.createdAt)
											.toLocaleDateString('es-ES', {
												day: '2-digit',
												month: 'short',
												year: 'numeric',
											})
											.replace('.', '')}{' '}
										{new Date(syncReport.createdAt).toLocaleTimeString('es-ES', {
											hour: '2-digit',
											minute: '2-digit',
											hour12: false,
										})}
									</TableCell>
									<TableCell>
										{syncReport.userProcessSync.first_name} {syncReport.userProcessSync.last_name}
									</TableCell>
									<TableCell>{syncReport.totalItems}</TableCell>
									<TableCell>{syncReport.successCount}</TableCell>
									<TableCell
										sx={{
											color: syncReport.errorCount >= 1 ? 'red' : 'inherit',
										}}
									>
										{syncReport.errorCount}
									</TableCell>
									<TableCell>
										<ButtonGroup size="small" aria-label="Small button group">
											<Button
												key="two"
												color="error"
												onClick={() => fetchExportSyncReport(syncReport._id ? syncReport._id : '')}
												variant="outlined"
											>
												{t(`common:download`)} csv
											</Button>
											{/*<Button key="one" onClick={() => handleOpenModal(syncReport)} variant="outlined">
												{t(`common:view`)}
											</Button>*/}
										</ButtonGroup>
									</TableCell>
									<SyncReportModal open={modalOpen} onClose={handleCloseModal} syncReport={selectedReport} />
								</TableRow>
							);
						},
						pagination: props.disabled
							? null
							: {
									count: props.syncReports.total,
									page: props.page,
									rowsPerPage: props.rowsPerPage,
									rowsPerPageOptions,
									onPageChange: handleChangePage,
									onRowsPerPageChange: handleChangeRowsPerPage,
							  },
					}}
				/>
			)}
		</FullLayout>
	);
}
